import { useAuthQuery } from 'features/common/apiUtils/queryHelper';
import { groupsApiConfig } from 'features/user-groups/api/config';

export interface OrgGroupMember {
  id: string;
  company: string;
  email: string;
  givenName?: string;
  familyName?: string;
  lastLogin?: string;
  picture?: string;
}

interface OrgGroupMemberResponse {
  data: OrgGroupMember[];
  pageNumber?: number;
  totalRecords?: number;
}

const {
  get_group_members: { url, scope, queryKey },
} = groupsApiConfig;

// Allowlist of server-side sortable fields (UI field ID → API field name)
const sortFieldMap: Record<string, string> = {
  givenName: 'givenName',
  familyName: 'familyName',
  email: 'email',
  lastLogin: 'lastLogin',
};

export const useOrgGroupMembersList = (
  groupId: string,
  params?: {
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortDesc?: boolean;
    query?: string;
    enabled?: boolean;
  },
) => {
  const {
    page = 1,
    pageSize = 10,
    sortField,
    sortDesc = false,
    query,
    enabled = true,
  } = params ?? {};
  const sortFieldName = sortField ? sortFieldMap[sortField] : undefined;

  // Build URL with pagination, optional sorting, and optional search query
  let apiUrl = url(groupId, page) + `&pagesize=${pageSize}`;
  if (sortFieldName) {
    apiUrl += `&sortBy=${sortFieldName}:${sortDesc ? '-1' : '1'}`;
  }
  if (query) {
    apiUrl += `&query=${encodeURIComponent(query)}`;
  }

  return useAuthQuery<OrgGroupMemberResponse>(
    [
      ...queryKey,
      groupId,
      page,
      pageSize,
      sortFieldName ? { sort: sortFieldName, desc: sortDesc } : null,
      query,
    ],
    async (axiosInstance) => {
      const response = await axiosInstance?.get<OrgGroupMemberResponse>(apiUrl);
      return response?.data ?? { data: [], totalRecords: 0 };
    },
    { enabled, keepPreviousData: true },
    scope,
  );
};
